import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { clearApiCache } from "@/lib/api";

const AuthContext = createContext(null);

function cacheUser(data) {
  try {
    localStorage.setItem("iacover_user", JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user

  const check = useCallback(async () => {
    // Pendant le retour d'Emergent Google Auth, on laisse AuthCallback échanger le session_id.
    if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      cacheUser(data);
    } catch (e) {
      // Hors-ligne : on restaure la dernière session connue pour permettre la consultation.
      if (!e.response) {
        const raw = localStorage.getItem("iacover_user");
        if (raw) {
          try {
            setUser(JSON.parse(raw));
            return;
          } catch {
            /* cache corrompu */
          }
        }
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    cacheUser(data);
  };

  const register = async (name, email, password, seedDemo = true) => {
    const { data } = await api.post("/auth/register", { name, email, password, seed_demo: seedDemo });
    setUser(data);
    cacheUser(data);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    clearApiCache();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
